import { useCallback, useEffect, useMemo, useState } from 'react';

import { TreadAgentSkillBasic } from '~hooks/useTreadAgent/models';

// Requiring the "/" to start a word keeps "12/05" and URLs from opening the menu.
const SLASH_COMMAND_PATTERN = /(^|\s)\/([a-z0-9-]*)$/i;

interface UseSkillPickerProps {
  onValueChange: (value: string) => void;
  skills: TreadAgentSkillBasic[];
  value: string;
}

export default function useSkillPicker({
  onValueChange,
  skills,
  value,
}: UseSkillPickerProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  // The toolbar button opens the whole list without a slash command in the draft.
  const [isBrowsing, setIsBrowsing] = useState(false);

  const typedSkillName = useMemo(() => {
    const match = value.match(SLASH_COMMAND_PATTERN);

    return match ? match[2].toLowerCase() : null;
  }, [value]);

  const matchingSkills = useMemo(() => {
    if (typedSkillName === null) {
      return isBrowsing ? skills : [];
    }

    return skills.filter((skill) => skill.name.toLowerCase().includes(typedSkillName));
  }, [isBrowsing, skills, typedSkillName]);

  const isOpen = !isDismissed && matchingSkills.length > 0;

  useEffect(() => {
    setActiveIndex(0);

    if (typedSkillName === null) {
      setIsDismissed(false);
    }
  }, [matchingSkills.length, typedSkillName]);

  // Any edit to the draft takes over from the button — either it starts a slash command,
  // which drives the menu itself, or the user has moved on.
  useEffect(() => {
    setIsBrowsing(false);
  }, [value]);

  const selectSkill = useCallback(
    (skillName: string) => {
      setIsBrowsing(false);

      // The trailing space ends the run, which is what closes the menu.
      const command = `/${skillName} `;

      if (typedSkillName === null) {
        onValueChange(
          !value || value.endsWith(' ') ? value + command : `${value} ${command}`,
        );

        return;
      }

      onValueChange(
        value.replace(SLASH_COMMAND_PATTERN, (_run, leading) => leading + command),
      );
    },
    [onValueChange, typedSkillName, value],
  );

  const browseSkills = useCallback(() => {
    setIsDismissed(false);
    setIsBrowsing(true);
  }, []);

  const dismissPicker = useCallback(() => {
    setIsBrowsing(false);
    setIsDismissed(true);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // An IME candidate window owns Enter and the arrows while a composition is running.
      if (!isOpen || event.nativeEvent.isComposing) {
        return false;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        // The expanded view collapses on any Escape that reaches the window.
        event.stopPropagation();
        dismissPicker();

        return true;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((previous) => (previous + 1) % matchingSkills.length);

        return true;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex(
          (previous) => (previous - 1 + matchingSkills.length) % matchingSkills.length,
        );

        return true;
      }

      // Shift+Enter is the composer's newline, so the picker leaves it alone.
      const isSelectKey =
        (event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab';

      if (isSelectKey) {
        event.preventDefault();
        selectSkill(matchingSkills[activeIndex].name);

        return true;
      }

      return false;
    },
    [activeIndex, dismissPicker, isOpen, matchingSkills, selectSkill],
  );

  return {
    activeIndex,
    browseSkills,
    dismissPicker,
    handleKeyDown,
    isOpen,
    matchingSkills,
    selectSkill,
    setActiveIndex,
  };
}
